import React, { useEffect, useState, useCallback } from 'react';
import { Mail, Phone, MapPin, Menu, X } from 'lucide-react';
import HomePage from './landing/HomePage';
import { ProduktPage, UeberUnsPage, KontaktPage } from './landing/SubPages';
import { BlogList, BlogArticle, ARTICLES } from './landing/BlogPage';
import LandingBackground from './LandingBackground';

// SEO meta per page
const SEO = {
  home: {
    title: 'Leistungsverzeichnis automatisch erstellen | LAIzyBlueprint',
    description: 'LAIzyBlueprint erstellt aus PDF-Grundrissen automatisch ein vollständiges, VOB-konformes Leistungsverzeichnis. LV Software für Bau, Architektur und Planung — von LAIzyones.',
  },
  produkt: {
    title: 'Produkt — LV Software Bau | LAIzyBlueprint',
    description: 'LAIzyBlueprint ist die LV Software für die Bauplanung: automatische Grundrissanalyse, strukturierte LV-Erstellung, Mengen-genau und VOB/C-konform. Grundriss in Leistungsverzeichnis in unter 60 Sekunden.',
  },
  'ueber-uns': {
    title: 'Über uns | LAIzyBlueprint von LAIzyones',
    description: 'LAIzyBlueprint ist das Produkt der LAIzyones — aus realem Bedarf von Bauunternehmen, Baustoffhändlern und Handwerkern entwickelt. Inhaber David Ehrle, operative Projektleitung Philipp-Arsim Nuraj.',
  },
  blog: {
    title: 'Blog & Wissensbereich | LAIzyBlueprint',
    description: 'Praxisnahe Artikel zu Digitalisierung im Bauwesen, Leistungsverzeichnis automatisch erstellen, Mengenermittlung und Bauplanung automatisieren.',
  },
  kontakt: {
    title: 'Kontakt — Demo anfragen | LAIzyBlueprint',
    description: 'Demo anfragen oder unverbindlich Kontakt aufnehmen. LV Software für Bauunternehmen, Baustoffhändler, Handwerker, Architekten, Planungsbüros und Projektentwickler.',
  },
};

function applySeo(page, title, description) {
  // Title
  document.title = title;
  // Description meta
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', description);
  // Open Graph
  let og = document.querySelector('meta[property="og:title"]');
  if (!og) {
    og = document.createElement('meta');
    og.setAttribute('property', 'og:title');
    document.head.appendChild(og);
  }
  og.setAttribute('content', title);
  let ogd = document.querySelector('meta[property="og:description"]');
  if (!ogd) {
    ogd = document.createElement('meta');
    ogd.setAttribute('property', 'og:description');
    document.head.appendChild(ogd);
  }
  ogd.setAttribute('content', description);
}

function NavBar({ page, navigate, onLogin, onRegister }) {
  const [open, setOpen] = useState(false);
  const link = (key, label) => (
    <button
      key={key}
      onClick={() => { navigate(key); setOpen(false); }}
      data-testid={`nav-${key}`}
      className={`px-1 transition-colors ${
        page === key || (key === 'blog' && page.startsWith('blog'))
          ? 'text-white'
          : 'text-zinc-400 hover:text-white'
      }`}
    >
      {label}
    </button>
  );
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/60 backdrop-blur-2xl border-b border-zinc-800/30">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <button onClick={() => navigate('home')} data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/logo.png" alt="LAIzyBlueprint" className="h-10" />
          <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            LAIzyBlueprint
          </span>
        </button>
        <div className="hidden md:flex items-center gap-8 text-sm">
          {link('home', 'Start')}
          {link('produkt', 'Produkt')}
          {link('ueber-uns', 'Über uns')}
          {link('blog', 'Blog')}
          {link('kontakt', 'Kontakt')}
        </div>
        <div className="hidden md:flex items-center gap-3">
          <button onClick={onLogin} data-testid="nav-login" className="px-4 py-2 text-sm text-zinc-300 hover:text-white font-medium">Anmelden</button>
          <button onClick={onRegister} data-testid="nav-register" className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-lg transition-colors">
            Jetzt testen
          </button>
        </div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="md:hidden p-2 text-zinc-300"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-zinc-800/40 bg-zinc-950 px-6 py-4 flex flex-col gap-3 text-sm">
          {link('home', 'Start')}
          {link('produkt', 'Produkt')}
          {link('ueber-uns', 'Über uns')}
          {link('blog', 'Blog')}
          {link('kontakt', 'Kontakt')}
          <div className="border-t border-zinc-800/40 pt-3 flex gap-3">
            <button onClick={onLogin} className="flex-1 py-2 text-zinc-300 border border-zinc-700 rounded-lg">Anmelden</button>
            <button onClick={onRegister} className="flex-1 py-2 bg-blue-600 text-white rounded-lg">Jetzt testen</button>
          </div>
        </div>
      )}
    </nav>
  );
}

function Footer({ navigate }) {
  return (
    <footer className="py-12 px-6 mt-auto relative" style={{ zIndex: 5 }}>
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-10">
          <div>
            <button onClick={() => navigate('home')} className="flex items-center gap-2 mb-3">
              <img src="/logo.png" alt="LAIzyBlueprint" className="h-6" />
              <span className="font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>LAIzyBlueprint</span>
            </button>
            <p className="text-zinc-500 text-sm leading-relaxed">
              LV Software für Bau, Architektur und Planung. Aus echtem Bedarf entwickelt — ein Produkt der LAIzyones.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-zinc-300 text-sm mb-3">Produkt</h4>
            <div className="space-y-2 text-sm text-zinc-500">
              <button onClick={() => navigate('produkt')} className="block hover:text-zinc-300 transition-colors">Funktionsweise</button>
              <button onClick={() => { navigate('home'); setTimeout(() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="block hover:text-zinc-300 transition-colors">Preise</button>
              <button onClick={() => { navigate('home'); setTimeout(() => document.getElementById('roi')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="block hover:text-zinc-300 transition-colors">ROI-Rechner</button>
              <button onClick={() => navigate('blog')} className="block hover:text-zinc-300 transition-colors">Blog</button>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-zinc-300 text-sm mb-3">Unternehmen</h4>
            <div className="space-y-2 text-sm text-zinc-500">
              <button onClick={() => navigate('ueber-uns')} className="block hover:text-zinc-300 transition-colors">Über uns</button>
              <button onClick={() => navigate('kontakt')} className="block hover:text-zinc-300 transition-colors">Kontakt</button>
              <a href="https://www.laizyones.de" target="_blank" rel="noopener noreferrer" className="block hover:text-zinc-300 transition-colors">laizyones.de</a>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-zinc-300 text-sm mb-3">Kontakt</h4>
            <div className="space-y-2 text-sm text-zinc-500">
              <a href="mailto:info@laizyones.de" className="flex items-center gap-2 hover:text-zinc-300"><Mail className="w-3.5 h-3.5" /> info@laizyones.de</a>
              <a href="tel:+491797615911" className="flex items-center gap-2 hover:text-zinc-300"><Phone className="w-3.5 h-3.5" /> 0179 7615911</a>
              <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> Wellen, Deutschland</div>
            </div>
          </div>
        </div>
        <div className="border-t border-zinc-800/40 pt-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-zinc-600">
          <span>&copy; {new Date().getFullYear()} LAIzyBlueprint ist ein Produkt der LAIzyones · Einzelunternehmen, Inhaber David Ehrle</span>
          <div className="flex gap-5">
            <a href="https://www.laizyones.de" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400">AGB</a>
            <a href="https://www.laizyones.de" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400">Impressum</a>
            <a href="https://www.laizyones.de" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400">Datenschutz</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function LandingPage({ onLogin, onRegister }) {
  const [page, setPage] = useState('home');

  const navigate = useCallback((target) => {
    setPage(target);
    // Scroll to top on page change
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Apply SEO based on current page
  useEffect(() => {
    let key = page;
    let extraDesc = null;
    let extraTitle = null;
    if (page.startsWith('blog/')) {
      const slug = page.split('/')[1];
      const article = ARTICLES.find((a) => a.slug === slug);
      if (article) {
        extraTitle = `${article.title} | LAIzyBlueprint Blog`;
        extraDesc = article.description;
      }
      key = 'blog';
    }
    const seo = SEO[key] || SEO.home;
    applySeo(key, extraTitle || seo.title, extraDesc || seo.description);
  }, [page]);

  let content;
  if (page === 'produkt') content = <ProduktPage onRegister={onRegister} navigate={navigate} />;
  else if (page === 'ueber-uns') content = <UeberUnsPage onRegister={onRegister} navigate={navigate} />;
  else if (page === 'kontakt') content = <KontaktPage />;
  else if (page === 'blog') content = <BlogList navigate={navigate} />;
  else if (page.startsWith('blog/')) {
    const slug = page.split('/')[1];
    content = <BlogArticle slug={slug} navigate={navigate} onRegister={onRegister} />;
  } else content = <HomePage onLogin={onLogin} onRegister={onRegister} navigate={navigate} />;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" style={{ fontFamily: "'DM Sans', 'Space Grotesk', system-ui, sans-serif" }}>
      <LandingBackground />
      <NavBar page={page} navigate={navigate} onLogin={onLogin} onRegister={onRegister} />
      <main className="flex-1 relative" style={{ zIndex: 5 }}>{content}</main>
      <Footer navigate={navigate} />
    </div>
  );
}
